/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { isUserRightToLeft } from 'js/lib/language';

import { Button, Grid, Typography, breakpoints, withTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon, ArrowPreviousIcon } from '@coursera/cds-icons';

import type { CmlContent } from 'bundles/cml';
import { CML } from 'bundles/cml';
import WeekStats from 'bundles/discussions/components/WeekStats';
import { getWeekDiscussionsUrl } from 'bundles/discussions/utils/discussionsUrl';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/discussions';

type ForumsV2DiscussionsWeekHeroUnitProps = {
  title: string;
  description: CmlContent;
  weekNumber?: number;
  isLoading?: boolean;
  lastAnsweredAt?: number;
  forumQuestionCount?: number;
  theme: Theme;
};

class DiscussionsWeekHeroUnit extends React.Component<ForumsV2DiscussionsWeekHeroUnitProps> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof DiscussionsWeekHeroUnit.contextTypes>;

  goToForum = () => {
    const { router } = this.context;
    router.push(getWeekDiscussionsUrl(this.props.weekNumber));
  };

  render() {
    const { weekNumber, isLoading, lastAnsweredAt, forumQuestionCount, title, description } = this.props;
    if (!weekNumber) {
      return null;
    }
    const isRtl = isUserRightToLeft();
    const arrowIcon = isRtl ? <ArrowPreviousIcon size="small" /> : <ArrowNextIcon size="small" />;
    return (
      <div
        css={css`
          padding: var(--cds-spacing-300);
          margin: 0 !important;
        `} // conflicting styles from main class name
        className="card-no-action rc-DiscussionsWeekHeroUnit"
      >
        {isLoading ? (
          <div className="message align-horizontal-center">
            <i className="cif-spinner cif-spin cif-2x" />
          </div>
        ) : (
          <Grid
            container
            item
            css={css`
              justify-content: space-between;
            `}
          >
            <Grid item>
              {/* This aria-label here is redundant, however it is necessary for the Chromevox reader on Chromebook to properly read and announce the text */}
              <Typography variant="h2semibold" aria-label={title} className="card-headline-text">
                {title}
              </Typography>
              <CML
                cml={description}
                css={css`
                  margin: var(--cds-spacing-200) 0 var(--cds-spacing-50) 0;
                `}
                isCdsEnabled={true}
              />
              <WeekStats lastAnsweredAt={lastAnsweredAt} forumQuestionCount={forumQuestionCount} />
            </Grid>

            <Grid
              item
              css={css`
                /* stylelint-disable-next-line block-no-empty */
                ${breakpoints.up('sm')} {
                }
                ${breakpoints.down('sm')} {
                  padding: var(--cds-spacing-300) 0 0 0;
                }
              `}
            >
              <Button
                component={TrackedLink2}
                size="medium"
                variant="primary"
                href={getWeekDiscussionsUrl(weekNumber)}
                trackingName="discussions_hero_unit"
                icon={arrowIcon}
                iconPosition="after"
              >
                {_t('Go to forum')}
              </Button>
            </Grid>
          </Grid>
        )}
      </div>
    );
  }
}

export default withTheme(DiscussionsWeekHeroUnit);
